import { useEffect, useRef, useState } from "react";
import type { Fremdtickets, FremdKarte } from "@kundeninfo/api-client";
import { SONDER_ZUSTAENDIG } from "./boardSpeicher.js";
import type { BoardFilter } from "./boardSpeicher.js";
import { readChoice, rememberValue } from "../../browserMemory.js";
import type { FremdticketsClient } from "../fremd/fremdClient.js";

/**
 * FREMDTICKETS IN BOARD UND LISTE (Spec Tickets, Nachtrag 1f, F142, F143): wessen Fremdtickets gezeigt werden, die
 * feste Kennung der Fremdspalte, kleine Hilfen und das Laden (beim Öffnen, bei Fokus-Rückkehr, alle 60 s — wie das
 * Board, F20; der Server liest dabei nur den Spiegel, nie das Quellsystem).
 */
export const FREMD_SPALTE_ID = "fremdtickets";
export const FREMD_SYSTEM: Record<FremdKarte["system"], string> = { GITLAB: "GitLab", YOUTRACK: "YouTrack" };
/** Status deutsch aus der Statusart (FREMD_STATUSARTEN der Domäne, über den Vertrag typgleich) — nie der Rohwert
 *  des Quellsystems („opened“, „Open“; Sichtprobe 1f, Befund 6). Wörter wie bei den Verknüpfungen. */
export const FREMD_STATUSART: Record<FremdKarte["remoteStateKind"], string> =
  { OPEN: "Offen", IN_PROGRESS: "In Arbeit", CLOSED: "Geschlossen", UNKNOWN: "Unbekannt" };

/**
 * Wessen Fremdtickets (F142): ohne Filter die eigenen; „Zuständig" auf einer anderen Person → für ADMIN deren,
 * sonst keine; Kunde, Team und „nur verknüpfte" kann kein Fremdticket erfüllen → keine. Das Unternehmen blendet
 * nicht aus: Fremdtickets gehören der Person. `null` = ausgeblendet.
 */
export function fremdPerson(f: BoardFilter, ich: { id: string; istAdmin?: boolean }): string | null {
  const team = (f as BoardFilter & { team?: string }).team ?? "";
  if (f.customer !== "" || f.linked || team !== "") return null;
  if (f.assignee === "" || f.assignee.toLowerCase() === ich.id.toLowerCase()) return ich.id;
  // Sonderwerte nennen keine Person: Niemand, Warteschlange meiner Teams, Von mir delegiert — ein Fremdticket gehört immer
  // einer Person (E442), die Kennung gäbe der Abruf sonst als `?user=queue` weiter.
  if (SONDER_ZUSTAENDIG.includes(f.assignee)) return null;
  return ich.istAdmin === true ? f.assignee : null;
}

export function minutenText(m: number): string {
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h === 0 ? `${r} min` : r === 0 ? `${h} h` : `${h} h ${r} min`;
}

export function fremdSuche<T extends { externalKey: string; title: string }>(items: readonly T[], q: string): T[] {
  const s = q.trim().toLowerCase();
  return s === "" ? [...items] : items.filter((k) => k.externalKey.toLowerCase().includes(s) || k.title.toLowerCase().includes(s));
}

export function hatFremdtickets(c: object): c is FremdticketsClient {
  return typeof (c as Partial<FremdticketsClient>).listFremdtickets === "function";
}

/**
 * Lädt die Fremdtickets der Person `person` (eigene: ohne `user`). `null` = nichts zu zeigen. Ein Ladefehler im
 * Takt lässt den letzten Stand DERSELBEN Person stehen — sonst verschwände die Spalte für eine Minute und das
 * Board spränge (Hausregel: kein Wegspringen); erst ein Personenwechsel räumt ihn. Läuft eine Drossel
 * (`naechsterManuellAb`), lädt der Haken zu ihrem Ende einmal nach, damit „Jetzt abgleichen" sich selbst löst.
 */
export function useFremdtickets(client: FremdticketsClient | undefined, person: string | null, ichId: string,
                                neuLaden: number | undefined): { daten: Fremdtickets | null; neu: () => void } {
  const [stand, setStand] = useState<{ fuer: string; daten: Fremdtickets } | null>(null);
  const [takt, setTakt] = useState(0);
  const lauf = useRef(0);
  useEffect(() => {
    const nr = ++lauf.current;
    if (client === undefined || person === null) { setStand(null); return; }
    client.listFremdtickets(person === ichId ? undefined : person)
      .then((d) => { if (nr === lauf.current) setStand({ fuer: person, daten: d }); })
      .catch(() => { if (nr === lauf.current) setStand((alt) => (alt !== null && alt.fuer === person ? alt : null)); });
  }, [client, person, ichId, neuLaden, takt]);
  useEffect(() => {
    const uhr = setInterval(() => setTakt((x) => x + 1), 60_000);
    const fokus = () => setTakt((x) => x + 1);
    window.addEventListener("focus", fokus);
    return () => { clearInterval(uhr); window.removeEventListener("focus", fokus); };
  }, []);
  const daten = stand !== null && client !== undefined && stand.fuer === person ? stand.daten : null;
  const sperreBis = daten?.naechsterManuellAb ?? null;
  useEffect(() => {
    if (sperreBis === null) return undefined;
    const warten = Date.parse(sperreBis) - Date.now();
    if (!(warten > 0)) return undefined;
    const t = setTimeout(() => setTakt((x) => x + 1), Math.min(warten + 500, 3_600_000));
    return () => clearTimeout(t);
  }, [sperreBis]);
  return { daten, neu: () => setTakt((x) => x + 1) };
}

const UHRZEIT = new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit" });
const TAG = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

/** „14:32" — die Uhrzeit eines Zeitpunkts (Sperre „Wieder möglich ab …", wie unter Einstellungen). */
export function uhrzeitText(iso: string): string {
  return UHRZEIT.format(new Date(iso));
}

/** „Stand von 14:02" (heute) bzw. „Stand von 17.10.2026 14:02" (F141, F144): wie alt der Spiegel ist. */
export function standText(iso: string, jetzt: Date = new Date()): string {
  const d = new Date(iso);
  return TAG.format(d) === TAG.format(jetzt) ? `Stand von ${uhrzeitText(iso)}` : `Stand von ${TAG.format(d)} ${uhrzeitText(iso)}`;
}

/** Ob der Abschnitt „Fremdtickets" der Liste offen ist — Browserzustand (F19), Vorgabe offen. */
const OFFEN_SCHLUESSEL = "ki-tickets-fremd-offen";
export function leseFremdOffen(): boolean {
  return readChoice(OFFEN_SCHLUESSEL, ["0", "1"], "1") === "1";
}
export function merkeFremdOffen(offen: boolean): void {
  rememberValue(OFFEN_SCHLUESSEL, offen ? "1" : "0");
}
